<div class="basket-audit">
  <!-- Header -->
  <div class="row mb-4 align-items-center">
    <div class="col-md-6">
      <h4>Basket Audit Trail</h4>
      <p class="text-muted">POS basket modifications for theft-prevention monitoring</p>
    </div>
    <div class="col-md-6 text-end">
      <span class="text-muted me-3" *ngIf="!isLoading">
        {{ totalEvents }} event{{ totalEvents !== 1 ? 's' : '' }} found
      </span>
      <button class="btn btn-outline-secondary btn-sm" (click)="loadEvents()">
        <nb-icon icon="refresh-outline"></nb-icon>
        Refresh
      </button>
    </div>
  </div>

  <!-- Filters -->
  <nb-card class="mb-4">
    <nb-card-body>
      <div class="row align-items-end g-3">
        <div class="col-md-3">
          <label class="form-label">Start Date</label>
          <input
            type="date"
            class="form-control"
            [(ngModel)]="startDate"
            (change)="onFilterChange()">
        </div>
        <div class="col-md-3">
          <label class="form-label">End Date</label>
          <input
            type="date"
            class="form-control"
            [(ngModel)]="endDate"
            (change)="onFilterChange()">
        </div>
        <div class="col-md-3">
          <label class="form-label">Event Type</label>
          <select class="form-select" [(ngModel)]="selectedEventType" (change)="onFilterChange()">
            <option value="">All Events</option>
            <option value="remove_item">Remove Item</option>
            <option value="clear_cart">Clear Cart</option>
          </select>
        </div>
        <div class="col-md-3 d-flex gap-2">
          <button class="btn btn-outline-secondary w-100" (click)="resetFilters()">
            Reset Filters
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>

  <!-- Table -->
  <nb-card>
    <nb-card-body class="p-0">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>Timestamp</th>
              <th>Event</th>
              <th>Reason</th>
              <th>Payment</th>
              <th>Terminal</th>
              <th>Manager ID</th>
              <th>Customer</th>
              <th>Products</th>
              <th>Cart ID</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let event of events; trackBy: trackByAuditLogId">
              <td class="text-nowrap">
                {{ event.timestamp | date:'dd/MM/yyyy HH:mm' }}
              </td>
              <td>
                <span
                  *ngIf="event.eventType === 'remove_item'"
                  class="badge bg-warning text-dark">
                  Remove Item
                </span>
                <span
                  *ngIf="event.eventType === 'clear_cart'"
                  class="badge bg-danger">
                  Clear Cart
                </span>
                <span
                  *ngIf="event.eventType !== 'remove_item' && event.eventType !== 'clear_cart'"
                  class="badge bg-secondary">
                  {{ event.eventType }}
                </span>
              </td>
              <td>
                <span
                  *ngIf="shouldShowClearRemoveBadges(event) && getReasonLabel(event.reason); else unknownReason"
                  class="badge"
                  [ngClass]="getReasonBadgeClass(event.reason)">
                  {{ getReasonLabel(event.reason) }}
                </span>
                <ng-template #unknownReason>
                  <span *ngIf="shouldShowClearRemoveBadges(event)" class="text-muted small">Unknown</span>
                  <span *ngIf="!shouldShowClearRemoveBadges(event)" class="text-muted">—</span>
                </ng-template>
              </td>
              <td>
                <span
                  *ngIf="shouldShowClearRemoveBadges(event) && isNotPaidWithin15Min(event) === true"
                  class="badge bg-danger"
                  nbTooltip="Cleared items were not followed by a completed order within 15 minutes">
                  Not paid within 15 min
                </span>
                <span
                  *ngIf="shouldShowClearRemoveBadges(event) && isNotPaidWithin15Min(event) === false"
                  class="badge bg-success">
                  Paid within 15 min
                </span>
                <span
                  *ngIf="shouldShowClearRemoveBadges(event) && isNotPaidWithin15Min(event) === null"
                  class="text-muted small">—</span>
                <span *ngIf="!shouldShowClearRemoveBadges(event)" class="text-muted">—</span>
              </td>
              <td>
                <span *ngIf="event.terminalName; else noTerminal">{{ event.terminalName }}</span>
                <ng-template #noTerminal>
                  <span class="text-muted" [title]="event.terminalId">
                    {{ truncateId(event.terminalId) }}
                  </span>
                </ng-template>
              </td>
              <td>
                <span class="text-muted font-monospace small" [title]="event.storeManagerId">
                  {{ truncateId(event.storeManagerId) }}
                </span>
              </td>
              <td>
                {{ event.customerName || (event.customerId ? truncateId(event.customerId) : 'Guest') }}
              </td>
              <td>
                <span class="badge bg-light text-dark border products-count-badge">
                  {{ event.items?.length || 0 }} product{{ event.items?.length !== 1 ? 's' : '' }}
                </span>
              </td>
              <td>
                <span class="font-monospace small text-muted" [title]="event.cartId">
                  {{ truncateId(event.cartId) }}
                </span>
              </td>
              <td>
                <button
                  class="btn btn-sm btn-outline-primary"
                  (click)="viewEventDetails(event)"
                  nbTooltip="View full event detail">
                  <nb-icon icon="eye-outline"></nb-icon>
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoading" class="text-center p-4">
        <nb-spinner size="large"></nb-spinner>
        <p class="mt-2 text-muted">Loading audit events...</p>
      </div>

      <!-- Error State -->
      <div *ngIf="!isLoading && errorMessage" class="text-center p-4">
        <nb-icon icon="alert-circle-outline" class="text-danger" style="font-size: 2rem;"></nb-icon>
        <p class="mt-2 text-danger">{{ errorMessage }}</p>
        <button class="btn btn-outline-secondary btn-sm" (click)="loadEvents()">
          Try Again
        </button>
      </div>

      <!-- Empty State -->
      <div *ngIf="!isLoading && !errorMessage && events.length === 0" class="text-center p-4">
        <nb-icon icon="shield-outline" style="font-size: 2.5rem;" class="text-muted mb-2"></nb-icon>
        <h5>No basket modification events found</h5>
        <p class="text-muted">No remove-item or clear-cart events match the current filters.</p>
      </div>

      <!-- Pagination -->
      <div
        *ngIf="!isLoading && totalPages > 1"
        class="d-flex justify-content-between align-items-center p-3 border-top">
        <div>
          <span class="text-muted">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to
            {{ Math.min(currentPage * pageSize, totalEvents) }} of {{ totalEvents }} events
          </span>
        </div>
        <div>
          <button
            class="btn btn-outline-secondary me-2"
            [disabled]="currentPage === 1"
            (click)="onPageChange(currentPage - 1)">
            Previous
          </button>
          <span class="me-2">Page {{ currentPage }} of {{ totalPages }}</span>
          <button
            class="btn btn-outline-secondary"
            [disabled]="currentPage === totalPages"
            (click)="onPageChange(currentPage + 1)">
            Next
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>
</div>

<!-- Event Detail Modal -->
<nb-card *ngIf="showEventDetails" class="modal-overlay">
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <div class="d-flex align-items-center gap-3">
      <h5 class="mb-0">Basket Event Detail</h5>
      <span *ngIf="selectedEvent?.eventType === 'clear_cart'" class="badge bg-danger">Clear Cart</span>
      <span *ngIf="selectedEvent?.eventType === 'remove_item'" class="badge bg-warning text-dark">Remove Item</span>
      <span *ngIf="selectedEvent?.eventType !== 'clear_cart' && selectedEvent?.eventType !== 'remove_item'" class="badge bg-secondary">{{ selectedEvent?.eventType }}</span>
      <span class="text-muted small">{{ selectedEvent?.timestamp | date:'dd/MM/yyyy HH:mm:ss' }}</span>
    </div>
    <button class="btn btn-ghost" (click)="closeEventDetails()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>

  <nb-card-body *ngIf="selectedEvent" class="modal-body">
    <!-- Event Meta -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h6 class="section-label">Event Information</h6>
        <p><strong>Manager ID:</strong>
          <span class="font-monospace small ms-2" [title]="selectedEvent.storeManagerId">
            {{ selectedEvent.storeManagerId || '—' }}
          </span>
        </p>
        <p><strong>Terminal:</strong>
          <span class="ms-2">{{ selectedEvent.terminalName || selectedEvent.terminalId || '—' }}</span>
        </p>
        <p><strong>Customer:</strong>
          <span class="ms-2">{{ selectedEvent.customerName || (selectedEvent.customerId ? selectedEvent.customerId : 'Guest') }}</span>
        </p>
        <p><strong>Cart ID:</strong>
          <span class="font-monospace small ms-2" [title]="selectedEvent.cartId">
            {{ selectedEvent.cartId || '—' }}
          </span>
        </p>
        <p *ngIf="shouldShowClearRemoveBadges(selectedEvent)">
          <strong>Reason:</strong>
          <span
            *ngIf="getReasonLabel(selectedEvent.reason); else detailUnknownReason"
            class="badge ms-2"
            [ngClass]="getReasonBadgeClass(selectedEvent.reason)">
            {{ getReasonLabel(selectedEvent.reason) }}
          </span>
          <ng-template #detailUnknownReason>
            <span class="text-muted ms-2">Unknown</span>
          </ng-template>
        </p>
        <p *ngIf="shouldShowClearRemoveBadges(selectedEvent) && isNotPaidWithin15Min(selectedEvent) === true">
          <strong>Payment follow-up:</strong>
          <span class="badge bg-danger ms-2">Not paid within 15 min</span>
        </p>
        <p *ngIf="shouldShowClearRemoveBadges(selectedEvent) && isNotPaidWithin15Min(selectedEvent) === false">
          <strong>Payment follow-up:</strong>
          <span class="badge bg-success ms-2">Paid within 15 min</span>
        </p>
      </div>
    </div>

    <!-- Products Table -->
    <h6 class="section-label">
      Products
      <span class="badge bg-secondary ms-2">{{ selectedEvent.items?.length || 0 }}</span>
    </h6>

    <div *ngIf="selectedEvent.items && selectedEvent.items.length > 0; else noItems" class="table-responsive">
      <table class="table table-sm table-bordered">
        <thead class="table-light">
          <tr>
            <th>Product Name</th>
            <th>SKU</th>
            <th class="text-center">Qty</th>
            <th class="text-end">Unit Price</th>
            <th class="text-end">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of selectedEvent.items">
            <td>{{ item.productName || '—' }}</td>
            <td class="font-monospace small text-muted">{{ item.sku || '—' }}</td>
            <td class="text-center">{{ item.quantity }}</td>
            <td class="text-end">{{ formatCurrency(item.unitPrice) }}</td>
            <td class="text-end">{{ formatCurrency((item.quantity || 0) * (item.unitPrice || 0)) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="table-active fw-bold">
            <td colspan="4" class="text-end">Total</td>
            <td class="text-end">{{ formatCurrency(getItemsTotal(selectedEvent.items)) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <ng-template #noItems>
      <p class="text-muted fst-italic">No product data recorded for this event.</p>
    </ng-template>
  </nb-card-body>
</nb-card>
